import { keepPreviousData, useQuery } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

export enum ReceiptBucket {
  PENDING_REVIEW = "pending_review",
  MATCHED = "matched",
  UNMATCHED = "unmatched",
}

export enum ReceiptSort {
  MOST_RECENT = "most_recent",
  OLDEST_FIRST = "oldest_first",
  CONFIDENCE = "confidence",
  HIGHEST_AMOUNT = "highest_amount",
  LOWEST_AMOUNT = "lowest_amount",
}

const VALID_RECEIPT_SORTS = new Set<string>(Object.values(ReceiptSort));

/** Parses a `sort` URL param, falling back to the default for anything unrecognized. */
export const parseReceiptSort = (raw: string | null): ReceiptSort =>
  raw && VALID_RECEIPT_SORTS.has(raw)
    ? (raw as ReceiptSort)
    : ReceiptSort.MOST_RECENT;

export interface SuggestedMatch {
  // Null on a ledger attach: the match came from the receipt's own column, not
  // a suggestion. Unlink routes by receipt uuid in that case.
  id: number | null;
  description: string | null;
  date: string | null;
  amount: string | null;
  delta: string | null;
}

export interface ReceiptListRow {
  uuid: string;
  status: string;
  merchant: string | null;
  transaction_date: string | null;
  amount: string | null;
  confidence_score: number;
  suggested_match: SuggestedMatch | null;
  no_match_reason?: "matcher_found_none" | "user_unlinked" | null;
  retry_expires_at?: string | null;
}

export interface ReceiptListCounts {
  pending: number;
  matched: number;
  unmatched: number;
}

export interface ReceiptsListResponse {
  receipts: ReceiptListRow[];
  meta: {
    total: number;
    counts: ReceiptListCounts;
  };
  links: {
    next: string | null;
    previous: string | null;
  };
}

export const RECEIPTS_DEFAULT_PAGE_SIZE = 25;

export interface UseReceiptsListOptions {
  bucket: ReceiptBucket;
  offset?: number;
  limit?: number;
  sort?: ReceiptSort;
  /** Skips the fetch entirely, e.g. for a user not yet known to have receipts access. */
  enabled?: boolean;
}

export interface UseReceiptsListResult {
  receipts: ReceiptListRow[];
  counts: ReceiptListCounts | null;
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const RECEIPTS_QUERY_KEY = "receiptsList";

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return "An unexpected error occurred";
}

export const useReceiptsList = (
  options: UseReceiptsListOptions
): UseReceiptsListResult => {
  const {
    bucket,
    offset = 0,
    limit = RECEIPTS_DEFAULT_PAGE_SIZE,
    sort,
    enabled = true,
  } = options;

  const params = new URLSearchParams();
  params.set("status", bucket);
  // Omit the default so unchanged URLs hit a single cached query key.
  if (sort && sort !== ReceiptSort.MOST_RECENT) params.set("sort", sort);
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  const path = `receipts/?${params.toString()}`;

  const { data, isLoading, error, refetch } = useQuery<ReceiptsListResponse>({
    queryKey: [
      RECEIPTS_QUERY_KEY,
      bucket,
      offset,
      limit,
      sort ?? ReceiptSort.MOST_RECENT,
    ],
    queryFn: async () => {
      const response = await ApiHttp.fetch<ReceiptsListResponse>(path);
      return response;
    },
    // Keep the prior page visible while the new offset/sort/bucket request
    // resolves, so the table doesn't flash an empty state during paging.
    placeholderData: keepPreviousData,
    retry: false,
    enabled,
  });

  const errorMessage: string | null = error ? extractErrorMessage(error) : null;

  return {
    receipts: data?.receipts ?? [],
    counts: data?.meta.counts ?? null,
    total: data?.meta.total ?? 0,
    isLoading,
    error: errorMessage,
    refetch: () => {
      refetch();
    },
  };
};
